// TaskDetail. Single-task view with status update, progress notes, and
// (when applicable) parent-task link + subtask table.
//
// Lane B adds: multi-owner R/A/C/I rail, cross-store variant toggle (with
// lock icon after first save), dependency hint banner, exam_scores panel
// for task type='training'.

import { useState } from 'react';
import { VAVA_SEED } from '../lib/seed.js';
import { parseHash, navigate } from '../lib/routing.js';
import {
  daysUntil, fmtDate, fmtShort, fmtDateTime, isOverdue, overdueDays
} from '../lib/dates.ts';
import { isBlocked } from '../lib/dependencies.ts';
import { isResponsible, getResponsibles } from '../lib/owners.ts';
import { isElevated } from '../lib/auth.ts';
import { Icon } from '../components/Icon.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { Prio, StatusPill, CatTag, Toast } from '../components/atoms.jsx';
import { DependencyHintBanner } from '../components/DependencyHint.jsx';
import { RaciRail } from '../components/RaciRail.jsx';
import { SplitTaskModal } from '../components/SplitTaskModal.jsx';
import { AttachmentsPanel } from '../components/AttachmentsPanel.jsx';
import { EditableField } from '../components/EditableField.jsx';
import { OwnersEditModal } from '../components/OwnersEditModal.jsx';
import { RichTextEditor } from '../components/RichTextEditor.jsx';
import { RichDescriptionEditor, RichContent } from '../components/RichDescription.jsx';
import { normalizeRichHtml } from '../lib/sanitize.ts';
import { SubtaskPanel } from '../components/SubtaskPanel.jsx';

export const TaskDetail = ({ user, tasks, updateTask, addUpdate, createTask, replaceOwners, deleteTask, staff: staffProp, groups = [] }) => {
  const [showSplit, setShowSplit] = useState(false);
  const [showOwnersEdit, setShowOwnersEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // staffProp comes from App. Falls back to staff for code paths
  // (legacy demo) that haven't been wired through yet.
  const staff = staffProp && staffProp.length > 0 ? staffProp : staff;
  const { parts } = parseHash();
  const taskId = parts[1];
  const task = tasks.find(t => t.id === taskId);
  const [newUpdate, setNewUpdate] = useState('');
  const [toast, setToast] = useState(null);
  const [peek, setPeek] = useState(null);   // 子项 being previewed in the side panel

  if (!task) {
    return (
      <>
        <button className="detail-back" onClick={() => navigate('/my-tasks')}><Icon name="back"/>返回</button>
        <div className="vava-card empty-state">
          <div className="emoji">🔍</div>
          <div className="title">待办不存在</div>
          <div className="sub">可能已被删除，或你没有访问权限</div>
          <button className="btn btn-primary" onClick={() => navigate(isElevated(user) ? '/admin' : '/my-tasks')}>返回列表</button>
        </div>
      </>
    );
  }

  const creator = staff.find(s => s.id === task.creatorId);
  // Anyone marked R on the task (or admin) can edit status.
  const canEditStatus = isElevated(user) || isResponsible(task, user.id);
  const blocked = isBlocked(task, tasks);

  async function changeStatus(newStatus) {
    if (newStatus === '已完成' && blocked) {
      setToast({ kind: 'error', msg: '此待办被依赖阻塞,无法标记完成' });
      return;
    }
    try {
      await updateTask(task.id, { status: newStatus });
      setToast({ kind: 'success', msg: `状态已更新为 ${newStatus}` });
    } catch {
      // App-level updateTask already surfaced the error toast
    }
  }

  async function submitUpdate() {
    const next = normalizeRichHtml(newUpdate);
    if (!next) return;
    try {
      await addUpdate(task.id, next);
      setNewUpdate('');
      setToast({ kind: 'success', msg: '进度已保存' });
    } catch {
      // App-level addUpdate already surfaced the error toast
    }
  }

  const days = daysUntil(task.dueDate);
  const overdue = isOverdue(task);
  const blockedLong = task.status === '阻塞' && task.updates && task.updates.length &&
                     (Date.now() - new Date(task.updates[0].at)) / (1000 * 60 * 60 * 24) > 7;

  const parent = task.parentId ? tasks.find(t => t.id === task.parentId) : null;
  const children = tasks.filter(t => t.parentId === task.id);
  const parentOwner = parent && staff.find(s => s.id === parent.assigneeId);

  return (
    <>
      {/* 返回 goes up one level: to the parent 待办 when this is a subtask,
          otherwise to the list. (Was always jumping to the home list.) */}
      <button className="detail-back"
              onClick={() => navigate(parent ? '/task/' + parent.id : (isElevated(user) ? '/admin' : '/my-tasks'))}>
        <Icon name="back"/>返回
      </button>

      {parent && (
        <div className="parent-link" onClick={() => navigate('/task/' + parent.id)}
             role="button" tabIndex={0}
             onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/task/' + parent.id)}>
          <div className="parent-link-label">属于待办 →</div>
          <div className="parent-link-main">
            <Prio value={parent.priority}/>
            <span className="parent-link-title">{parent.title}</span>
            <span className="parent-link-owner">
              <Avatar staff={parentOwner}/> {parentOwner?.name}
            </span>
          </div>
        </div>
      )}

      <div className="vava-page-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="vava-page-title" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Prio value={task.priority} />
            <EditableField value={task.title}
              onSave={(v) => updateTask(task.id, { title: v || task.title })}
              ariaLabel="待办标题"
              displayClassName="task-title-inline" />
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusPill value={task.status} />
            {(() => {
              const g = groups.find(gg => gg.id === task.groupId);
              return g ? <CatTag value={`${g.icon || ''} ${g.name}`.trim()} /> : <CatTag value={task.category || '未分类'} />;
            })()}
            {task.recurring !== '一次性' && <CatTag value={`循环 · ${task.recurring}`} />}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {createTask && (
            <button className="btn btn-secondary" onClick={() => setShowSplit(true)}>
              <Icon name="plus"/>拆分待办
            </button>
          )}
          {canEditStatus && (
            <select className="vava-select" style={{ width: 160 }} value={task.status}
                    onChange={(e) => changeStatus(e.target.value)} aria-label="修改状态">
              {VAVA_SEED.STATUSES.map(s => <option key={s} value={s}>改为 {s}</option>)}
            </select>
          )}
          {isElevated(user) && deleteTask && (
            <button className="btn btn-danger" onClick={() => setShowDelete(true)}
                    aria-label="删除待办" title="删除待办">
              <Icon name="trash" />删除
            </button>
          )}
        </div>
      </div>

      {overdue && (
        <div className="vava-alert red" role="alert">
          <div className="left"><Icon name="alert" className="icon" />此待办已逾期 <strong className="mono">{overdueDays(task)} 天</strong></div>
        </div>
      )}
      {blockedLong && (
        <div className="vava-alert amber" role="alert">
          <div className="left"><Icon name="alert" className="icon" />此待办阻塞已超过 7 天 · 建议联系相关人员解决</div>
        </div>
      )}
      <DependencyHintBanner task={task} allTasks={tasks} />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 20 }} className="detail-layout">
        <div className="vava-card">
          <h2 className="vava-card-title">待办详情</h2>
          <RichDescriptionEditor
            value={task.detail || ''}
            onSave={(v) => updateTask(task.id, { detail: v ?? '' })}
            taskId={task.id}
            staff={staff}
            placeholder="（点击添加描述 · 支持 @同事 和 粘贴图片）" />
          {task.blockReason && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--vava-red-bg)', borderRadius: 6, fontSize: 13 }}>
              <strong>阻塞原因：</strong>{task.blockReason}
            </div>
          )}
        </div>
        <div className="vava-card">
          <h2 className="vava-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>负责人 · RACI</span>
            <button className="raci-edit-btn" onClick={() => setShowOwnersEdit(true)} aria-label="编辑负责人">
              ✎ 编辑
            </button>
          </h2>
          <RaciRail task={task} staff={staff}/>
          <div style={{ borderTop: '1px solid var(--vava-border-soft)', marginTop: 14, paddingTop: 14 }}>
            <h2 className="vava-card-title" style={{ marginBottom: 8 }}>元信息</h2>
          </div>
          <div className="detail-row"><span className="label">创建人</span><span className="value">{creator?.name}</span></div>
          <div className="detail-row"><span className="label">创建于</span><span className="value mono">{fmtDate(task.createdAt)}</span></div>
          <div className="detail-row">
            <span className="label">截止日期</span>
            <span className="value mono" style={{ color: overdue ? 'var(--vava-red)' : 'inherit' }}>
              <EditableField value={task.dueDate || ''}
                onSave={(v) => updateTask(task.id, { dueDate: v || null })}
                variant="date"
                placeholder="（设置截止）"
                ariaLabel="截止日期" />
              {task.dueDate && days !== null && (
                <span style={{ marginLeft: 6 }}>{overdue ? `· 逾期 ${Math.abs(days)} 天` : `· 还剩 ${days} 天`}</span>
              )}
            </span>
          </div>
          {task.completedAt && (
            <div className="detail-row"><span className="label">完成于</span><span className="value mono">{fmtDate(task.completedAt)}</span></div>
          )}
          <div className="detail-row">
            <span className="label">优先级</span>
            <span className="value">
              <EditableField value={task.priority}
                onSave={(v) => updateTask(task.id, { priority: v })}
                variant="select"
                options={[{value:'P0',label:'P0 紧急'},{value:'P1',label:'P1'},{value:'P2',label:'P2'}]}
                ariaLabel="优先级" />
            </span>
          </div>
          <div className="detail-row">
            <span className="label">分类</span>
            <span className="value">
              <EditableField value={task.groupId || ''}
                onSave={(v) => updateTask(task.id, { groupId: v || null })}
                variant="select"
                options={(groups || []).filter(g => !g.archived)
                  .sort((a,b) => a.displayOrder - b.displayOrder)
                  .map(g => ({ value: g.id, label: (g.icon ? g.icon + ' ' : '') + (g.parentId ? '— ' : '') + g.name }))}
                ariaLabel="分类" />
            </span>
          </div>
          {task.blockedBy && task.blockedBy.length > 0 && (
            <div className="detail-row">
              <span className="label">依赖</span>
              <span className="value" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {task.blockedBy.map(blockerId => {
                  const blocker = tasks.find(t => t.id === blockerId);
                  if (!blocker) return null;
                  const done = blocker.status === '已完成' || blocker.status === '已取消';
                  return (
                    <a key={blockerId} href={`#/task/${blockerId}`}
                       style={{ fontSize: 13, color: done ? 'var(--vava-text-muted)' : 'var(--vava-amber)',
                                textDecoration: done ? 'line-through' : 'underline' }}>
                      {done ? '✓' : '○'} {blocker.title}
                    </a>
                  );
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="vava-card">
        <AttachmentsPanel taskId={task.id} user={user} staff={staff} />
      </div>

      <div className="vava-card">
        <h2 className="vava-card-title">评论 & 进度</h2>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <RichTextEditor
              value={newUpdate}
              onChange={setNewUpdate}
              staff={staff}
              taskId={task.id}
              compact
              rows={2}
              placeholder="评论、进度更新、@同事 · ⌘+Enter 发送"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitUpdate(); } }} />
          </div>
          <button className="btn btn-primary" onClick={submitUpdate} disabled={!normalizeRichHtml(newUpdate)}>
            <Icon name="plus"/>发送
          </button>
        </div>
        {(!task.updates || task.updates.length === 0) ? (
          <div style={{ color: 'var(--vava-text-muted)', fontSize: 13, padding: '12px 0' }}>
            还没有评论 · 当第一个开口的人
          </div>
        ) : (
          <div className="update-list">
            {[...task.updates].reverse().map((u, i) => {
              const who = staff.find(s => s.id === u.by);
              const isMe = u.by === user.id;
              return (
                <div className={`update-item ${isMe ? 'update-mine' : ''}`} key={i}>
                  <div className="time">
                    {fmtDateTime(u.at)} · <strong>{who?.name || 'Unknown'}</strong>
                    {isMe && <span style={{ marginLeft: 6, color: 'var(--vava-tan-hover)', fontSize: 11 }}>(我)</span>}
                  </div>
                  <RichContent value={u.text} staff={staff} className="text" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {children.length > 0 && (
        <div className="vava-card" style={{ marginTop: 16 }}>
          <h2 className="vava-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>子项（按待办分配 · {children.length} 条）</span>
            <span className="mono" style={{ fontSize: 12, color: 'var(--vava-text-muted)', fontWeight: 400 }}>
              {children.filter(c => c.status === '已完成').length}/{children.length} 完成
            </span>
          </h2>
          <table className="vava-table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th style={{ width: 180 }}>负责人</th>
                <th>子项</th>
                <th style={{ width: 80 }}>优先级</th>
                <th style={{ width: 100 }}>截止</th>
                <th style={{ width: 100 }}>状态</th>
              </tr>
            </thead>
            <tbody>
              {children.map(c => {
                // All 负责人 (R), 主负责人 first; falls back to the legacy single
                // assignee for old tasks. Several 店长 stack as overlapping avatars.
                const rOwners = getResponsibles(c).map(o => ({ ...o, s: staff.find(x => x.id === o.staffId) }));
                const primary = rOwners[0];
                const over = isOverdue(c);
                return (
                  <tr key={c.id} className="clickable" onClick={() => setPeek(c)}>
                    <td>
                      {rOwners.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar staff={undefined}/>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>—</div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="subtree-owner-avatars" title={rOwners.map(o => o.s?.name || o.staffId).join('、')}>
                            {rOwners.map(o => (
                              <span key={o.staffId} className={`subtree-owner-av${o.isPrimary ? ' is-primary' : ''}`}>
                                <Avatar staff={o.s}/>
                                {o.isPrimary && rOwners.length > 1 && <span className="subtree-owner-star" aria-label="主负责人">★</span>}
                              </span>
                            ))}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {rOwners.length === 1 ? (primary.s?.name || primary.staffId) : `${primary.s?.name || primary.staffId} 等 ${rOwners.length} 人`}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--vava-text-muted)' }}>
                              {rOwners.length === 1 ? (primary.s?.title || '') : `${rOwners.length} 位负责人`}
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 500 }}>{c.title}</td>
                    <td><Prio value={c.priority}/></td>
                    <td className="mono" style={{ color: over ? 'var(--vava-red)' : 'inherit', fontWeight: over ? 600 : 400 }}>
                      {fmtShort(c.dueDate)}{over && ' · 逾期'}
                    </td>
                    <td><StatusPill value={c.status}/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isElevated(user) && (
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin?addChildOf=' + task.id)}>
                <Icon name="plus"/>加子项
              </button>
            </div>
          )}
        </div>
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
      {showSplit && createTask && (
        <SplitTaskModal parent={task} staff={staff}
                        onClose={() => setShowSplit(false)}
                        onCreate={createTask}/>
      )}
      {showOwnersEdit && replaceOwners && (
        <OwnersEditModal task={task} staff={staff}
                         replaceOwners={replaceOwners}
                         onClose={() => setShowOwnersEdit(false)} />
      )}
      {showDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true"
             onClick={(e) => { if (e.target === e.currentTarget && !deleting) setShowDelete(false); }}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>删除待办？</h2>
            <p style={{ margin: '0 0 8px', color: 'var(--vava-text-muted)', fontSize: 13 }}>
              将永久删除待办 <strong>《{task.title}》</strong>，包括其评论、附件、负责人和通知记录。
            </p>
            {children.length > 0 && (
              <p style={{ margin: '0 0 8px', color: 'var(--vava-red)', fontSize: 13 }}>
                此待办有 {children.length} 条子项，必须先删除或转移子项才能删除待办。
              </p>
            )}
            <p style={{ margin: '0 0 16px', color: 'var(--vava-text-muted)', fontSize: 12 }}>
              此操作不可撤销。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" disabled={deleting}
                      onClick={() => setShowDelete(false)}>取消</button>
              <button className="btn btn-danger" disabled={deleting || children.length > 0}
                      onClick={async () => {
                        setDeleting(true);
                        try { await deleteTask(task.id); }
                        catch { setDeleting(false); setShowDelete(false); }
                      }}>
                {deleting ? '删除中…' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {peek && (
        <SubtaskPanel task={tasks.find(t => t.id === peek.id) || peek} staff={staff}
                      updateTask={updateTask} addUpdate={addUpdate}
                      onClose={() => setPeek(null)}
                      onOpenFull={(id) => navigate('/task/' + id)} />
      )}
    </>
  );
};
